package com.rainbowclubs.rainbow.presentation.club

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.Club
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.common.MembershipStatusCard
import com.rainbowclubs.rainbow.presentation.common.bottomNavBar.BottomNavBar
import com.rainbowclubs.rainbow.presentation.common.bottomNavBar.BottomNavItem
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.accentGreen
import com.rainbowclubs.rainbow.presentation.theme.rainbowGradient
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.decodeToImageBitmap
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun ClubScreen(
    clubId: String,
    viewModel: ClubViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (ClubViewModel.ClubNavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    ClubScaffold(
        screenState = screenState,
        onRetryClick = viewModel::fetchClub,
        onHomeClick = viewModel::navigateToHome,
        onMessagesClick = viewModel::navigateToMessages,
        onMoreClick = viewModel::navigateToMore,
    )
}

@Composable
private fun ClubScaffold(
    screenState: ScreenState<ClubUiState>,
    onRetryClick: () -> Unit,
    onHomeClick: () -> Unit,
    onMessagesClick: () -> Unit,
    onMoreClick: () -> Unit,
) {
    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
        bottomBar = {
            BottomNavBar(
                selectedItem = BottomNavItem.Club,
                onHomeClick = onHomeClick,
                onClubClick = { },
                onMessagesClick = onMessagesClick,
                onMoreClick = onMoreClick,
            )
        },
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .padding(paddingValues)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = onRetryClick,
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> {
                    if (state.data.membershipStatus == MembershipStatus.APPROVED) {
                        ClubScreenContent(
                            uiState = state.data,
                        )
                    } else {
                        MembershipStatusCard(status = state.data.membershipStatus)
                    }
                }
            }
        }
    }
}

@Composable
private fun ClubScreenContent(
    uiState: ClubUiState,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        Spacer(modifier = Modifier.height(24.dp))

        Text(
            text = stringResource(Res.string.club_page_label),
            color = MaterialTheme.colorScheme.outline,
            style = MaterialTheme.typography.titleMedium,
            maxLines = 1,
        )

        Spacer(modifier = Modifier.height(24.dp))

        ClubImageCard(imageBytes = uiState.clubImageBytes)

        Spacer(modifier = Modifier.height(28.dp))

        Text(
            text = uiState.club.name,
            color = MaterialTheme.colorScheme.onBackground,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
        )

        Spacer(modifier = Modifier.height(14.dp))

        RainbowAccent()

        Spacer(modifier = Modifier.height(34.dp))

        Text(
            text = stringResource(Res.string.club_about_title),
            color = MaterialTheme.colorScheme.onBackground,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
        )

        Spacer(modifier = Modifier.height(14.dp))

        Text(
            text = uiState.club.description.orEmpty(),
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            style = MaterialTheme.typography.bodyLarge,
        )

        Spacer(modifier = Modifier.height(36.dp))
    }
}

@Composable
private fun ClubImageCard(
    imageBytes: ByteArray?,
) {
    val imageBitmap = remember(imageBytes) {
        imageBytes?.let { bytes ->
            runCatching { bytes.decodeToImageBitmap() }.getOrNull()
        }
    }

    Surface(
        modifier = Modifier
            .fillMaxWidth(),
        shape = RoundedCornerShape(28.dp),
        color = MaterialTheme.colorScheme.accentGreen.copy(alpha = 0.06f),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.accentGreen.copy(alpha = 0.18f)),
    ) {
        Box(modifier = Modifier.padding(16.dp)) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .clip(RoundedCornerShape(24.dp))
                    .background(MaterialTheme.colorScheme.rainbowGradient),
            ) {
                if (imageBitmap != null) {
                    Image(
                        bitmap = imageBitmap,
                        contentDescription = stringResource(Res.string.club_image_preview),
                        contentScale = ContentScale.Inside,
                        modifier = Modifier.fillMaxSize(),
                    )
                }

                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(
                            Brush.linearGradient(
                                colors = listOf(
                                    MaterialTheme.colorScheme.scrim.copy(alpha = 0.02f),
                                    MaterialTheme.colorScheme.scrim.copy(alpha = 0.18f),
                                )
                            )
                        ),
                )
            }
        }
    }
}

@Composable
private fun RainbowAccent() {
    Box(
        modifier = Modifier
            .width(160.dp)
            .height(6.dp)
            .clip(RoundedCornerShape(3.dp))
            .background(MaterialTheme.colorScheme.rainbowGradient),
    )
}

@Preview(name = "Club Screen")
@Composable
private fun ClubScreenPreview() {
    RainbowClubTheme {
        ClubScaffold(
            screenState = ScreenState.Content(sampleClubUiState()),
            onRetryClick = {},
            onHomeClick = {},
            onMessagesClick = {},
            onMoreClick = {},
        )
    }
}

@Preview(name = "Club Screen Loading")
@Composable
private fun ClubScaffoldLoadingPreview() {
    RainbowClubTheme {
        ClubScaffold(
            screenState = ScreenState.Loading,
            onRetryClick = {},
            onHomeClick = {},
            onMessagesClick = {},
            onMoreClick = {},
        )
    }
}

@Preview(name = "Club Content")
@Composable
private fun ClubScreenContentPreview() {
    RainbowClubTheme {
        Surface(color = MaterialTheme.colorScheme.background) {
            ClubScreenContent(uiState = sampleClubUiState())
        }
    }
}

@Preview(name = "Club Image Card")
@Composable
private fun ClubImageCardPreview() {
    RainbowClubTheme {
        Surface(color = MaterialTheme.colorScheme.background) {
            ClubImageCard(imageBytes = null)
        }
    }
}

@Preview(name = "Rainbow Accent")
@Composable
private fun RainbowAccentPreview() {
    RainbowClubTheme {
        Surface(color = MaterialTheme.colorScheme.background) {
            RainbowAccent()
        }
    }
}

private fun sampleClubUiState() = ClubUiState(
    club = Club(
        id = "club-preview",
        name = "Rainbow Makers Club",
        description = "A welcoming space for members to create, learn, and gather around shared interests. Join activities, meet other members, and stay connected with everything happening in the club.",
        membershipData = emptyList(),
        clubImageUrl = null,
    ),
    clubImageBytes = null,
    membershipStatus = MembershipStatus.APPROVED,
)
